import { FormControl } from '@mui/base';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { primaryBlue, white } from '@styles/colors';
import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import SelectRepeatModal from '../SelectRepeatModal/SelectRepeatModal';

type SelectDateTimeFormProps = {
  setStartTimestamp: Dispatch<SetStateAction<number | null>>;
  setEndTimestamp: Dispatch<SetStateAction<number | null>>;
  setFormattedDate: Dispatch<SetStateAction<string>>;
  setRepeatOccurence: Dispatch<SetStateAction<string | null>>;
  setRepeatDate: Dispatch<SetStateAction<Dayjs | null>>;
  setRepeat: Dispatch<SetStateAction<boolean>>;
  setShowRepeatModal: Dispatch<SetStateAction<boolean>>;
  setSelectedDays: Dispatch<SetStateAction<string[]>>;
  repeat: boolean;
  showRepeatModal: boolean;
  repeateDate: Dayjs | null;
  setRepeatEnd: Dispatch<SetStateAction<string>>;
  repeateOccurence: string | null;
  repeatEnd: string;
  selectedDays: string[];
  defaultDuration?: number;
  directionColumn?: boolean;
  notShowRepeat?: boolean;
  initialValues?: {
    date: Dayjs;
    startTime: Dayjs;
    endTime: Dayjs;
  };
};

const SelectDateTimeForm: FC<SelectDateTimeFormProps> = ({
  setRepeatOccurence,
  repeateOccurence,
  setRepeat,
  repeat,
  notShowRepeat,
  setShowRepeatModal,
  showRepeatModal,
  setSelectedDays,
  selectedDays,
  repeatEnd,
  repeateDate,
  setRepeatDate,
  setRepeatEnd,
  setStartTimestamp,
  setEndTimestamp,
  setFormattedDate,
  defaultDuration = 30,
  directionColumn = false,
  initialValues,
}) => {
  const theme = useTheme();
  const isSmOrSmaller = useMediaQuery(theme.breakpoints.down('sm'));

  const [date, setDate] = useState<Dayjs | null>(initialValues?.date ?? null);

  const [startTime, setStartTime] = useState<Dayjs | null>(
    initialValues?.startTime ?? null,
  );
  const [endTime, setEndTime] = useState<Dayjs | null>(
    initialValues?.endTime ?? null,
  );

  const getTimestamp = (time: Dayjs | null) =>
    time
      ? time
          .set('year', date?.year()!)
          .set('month', date?.month()!)
          .set('date', date?.date()!)
          .valueOf() / 1000
      : null;

  const handleDateUpdate = (newDate: Dayjs | null) => {
    setDate(newDate);
    setFormattedDate(newDate?.format('YYYY-MM-DD') ?? '');
    if (newDate && setSelectedDays) {
      const dayOfWeek = newDate.day();
      const dayMapping = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      setSelectedDays([dayMapping.toString()]);
    }
  };

  const handleUpdateStartTime = (newStartTime: Dayjs | null) => {
    if (newStartTime && date) {
      // Combine date and start time
      const newEndTime =
        dayjs(newStartTime).add(defaultDuration, 'minutes') ?? null;

      setStartTime(newStartTime);
      setEndTime(newEndTime);

      setStartTimestamp(getTimestamp(newStartTime));
      setEndTimestamp(getTimestamp(newEndTime));
    }
  };

  const handleUpdateEndTime = (newEndTime: Dayjs | null) => {
    if (newEndTime && date) {
      setEndTime(newEndTime);
      setEndTimestamp(getTimestamp(newEndTime));
    }
  };
  const mapToDayOfWeek = (item: string) => {
    switch (item) {
      case '6':
        return 'Sun';
      case '0':
        return 'Mon';
      case '1':
        return 'Tue';
      case '2':
        return 'Wed';
      case '3':
        return 'Thu';
      case '4':
        return 'Fri';
      case '5':
        return 'Sat';
      default:
        return 'Invalid day';
    }
  };

  const handleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeat(event.target.value === 'true' ? true : false);
    if (event.target.value === 'true') {
      setShowRepeatModal(true);
    }
    setRepeatOccurence(null);
    setRepeatDate(null);
    setSelectedDays(!date ? ['0'] : [...selectedDays]);
    setRepeatEnd('never');
  };

  useEffect(() => {
    setStartTimestamp(getTimestamp(startTime));
    setEndTimestamp(getTimestamp(endTime));
  }, [date]);

  return (
    <Stack
      direction={directionColumn || isSmOrSmaller ? 'column' : 'row'}
      spacing={2}
    >
      <Box>
        <Typography>Date:</Typography>
        <DatePicker
          disablePast
          value={date}
          onChange={handleDateUpdate}
          sx={{ width: '100%' }}
          format="MM/DD/YYYY"
        />
      </Box>
      <Stack direction={'row'} spacing={2}>
        <Box>
          <Typography>Start time:</Typography>
          <TimePicker
            value={startTime}
            onChange={handleUpdateStartTime}
            maxTime={endTime!}
            disabled={!date}
          />
        </Box>
        <Box>
          <Typography>End time:</Typography>
          <TimePicker
            value={endTime}
            onChange={handleUpdateEndTime}
            minTime={startTime!}
            disabled={!date}
          />
        </Box>
      </Stack>
      {!notShowRepeat && (
        <Box>
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={repeat}
              onChange={handleSwitch}
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="Does not Repeat"
              />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Tooltip
                  arrow
                  placement="bottom"
                  title={!date ? 'Please select date first' : ''}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Repeat Weekly"
                  />
                </Tooltip>
                {
                  repeat && (
                    // <IconButton sx={{
                    //   '& .css-i4bv87-MuiSvgIcon-root': {
                    //     fontSize: "10x"
                    //   }
                    // }} >
                    <EditIcon
                      sx={{
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: primaryBlue,
                      }}
                      onClick={() => setShowRepeatModal(true)}
                    />
                  )
                  // </IconButton>
                }
              </Stack>
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      {repeat && (
        <>
          <Typography>Repeat on:</Typography>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ flexWrap: 'wrap', gap: '5px' }}
          >
            {selectedDays
              .sort((a: string, b: string) => Number(a) - Number(b))
              .map((item) => {
                return (
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      display: 'inline-block',
                      fontSize: '0.8rem',
                      backgroundColor: primaryBlue,
                      color: white,
                      padding: '0.3rem 1rem',
                      borderRadius: 5,
                    }}
                  >
                    {mapToDayOfWeek(item)}
                  </Typography>
                );
              })}
          </Stack>
          <Typography>
            {repeatEnd === 'never' ? (
              <Typography>
                Will <b>never end</b>
              </Typography>
            ) : repeatEnd === 'after' && repeateOccurence ? (
              <Typography>
                Will end after <b>{repeateOccurence}</b> Occurrences
              </Typography>
            ) : (
              repeatEnd === 'on' &&
              repeateDate && (
                <Typography>
                  Will end on <b>{repeateDate?.format('YYYY-MM-DD')}</b>
                </Typography>
              )
            )}
          </Typography>
        </>
      )}
      {showRepeatModal && (
        <SelectRepeatModal
          isOpen={repeat}
          handleClose={() => {
            if (!repeat) {
              setRepeatOccurence(null);
              setRepeatDate(null);
              setSelectedDays(['1']);
              setRepeatEnd('never');
              setShowRepeatModal(false);
            } else {
              setShowRepeatModal(false);
              // setRepeat(false)
            }
          }}
          repeateOccurence={repeateOccurence}
          setRepeatOccurence={setRepeatOccurence}
          setRepeat={setRepeat}
          showRepeatModal={showRepeatModal}
          setShowRepeatModal={setShowRepeatModal}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          repeatEnd={repeatEnd}
          repeateDate={repeateDate}
          setRepeatDate={setRepeatDate}
          setRepeatEnd={setRepeatEnd}
        />
      )}
    </Stack>
  );
};

export default SelectDateTimeForm;
